// SAB-751: per-game lineup lock computation, extracted so it is testable and so
// the two call sites in useAddDrop cannot drift apart. They HAD drifted, and the
// copy that actually gates swaps was the one that failed open.
//
// FOUR invariants this file exists to hold:
//
//  1. FAIL CLOSED ON AN UNRESOLVABLE TEAM. A player whose team cannot be
//     resolved reads as LOCKED. The old code did
//     `if (teamName && lockedTeams.has(teamName))`, so an unresolvable team
//     meant "free to move" on a real-money contest. Matches the SAB-709
//     server-side posture and nfl-lineup's "no resolvable NFL team" guard.
//
//  2. NO UNBOUNDED LOOKUP. Locks compare TEAM IDS the roster rows already carry
//     against ids from `game_schedule` (date-bounded, ~15 rows/day). The old
//     implementation translated abbreviation -> full name via
//     `players .in('mlb_team_abbreviation', teamAbbrs)`, matching 4,800-6,300
//     rows against PostgREST's 1,000-row cap and silently dropping whole teams.
//     See SAB-752 for the wider class.
//
//  3. FAIL CLOSED ON AN UNRECOGNISED STATUS — and classify in CODE, not in SQL.
//     The original bug: the client matched `['In Progress','Final','Game Over']`
//     while `game_schedule.status` actually holds lowercase `scheduled` / `live`
//     / `final` / `postponed`. Nothing matched, so the in-progress branch was
//     dead and the lock NEVER FIRED — 302 rostered players sat in live games
//     unlocked. Critically, the status filter used to live in the SQL `.in(...)`,
//     which means an unrecognised status was not even FETCHED, so it could never
//     be classified as started. Filtering in SQL is fail-open by construction.
//     We now fetch every game for the date and decide here, where "unknown"
//     can be made to mean "locked".
//
// VOCABULARY (closed set, verified against both writers):
//   fetch-game-schedule/index.ts  mapStatus():        Preview->scheduled,
//     Live->live, Final->final, Postponed->postponed, default->scheduled
//   fetch-box-scores/index.ts     mapAbstractStatus(): same four, default->null
//     (skips the update, leaving the prior value)
//   matchup-engine/index.ts:1749 writes 'final'; inject-mock-stats writes 'final'
// Prod distribution 2026-08-20: final 1,930 / scheduled 102 / live 6.
//
//  4. A KNOWN-UNPLAYABLE STATUS UNLOCKS. Released / Free Agent / Voluntarily
//     Retired resolve unlocked: fail-closed guards IGNORANCE about a game, and
//     for these we are not ignorant. The vocabulary lives in
//     @shared/mlbPlayerStatus, imported by BOTH this file and daily-lineup so
//     the two controls cannot disagree (SAB-1159 is what happens when they do).
//
// NOTE: this is a CLIENT-SIDE control — a UX guard, not a security boundary.
//
// THE PARAGRAPH HERE USED TO SAY the server-side control was dead too, because
// `_shared/lineupLocks.ts:147` carries the same Title Case bug. The first half is
// true and the conclusion was wrong: it conflated two modules. `daily-lineup`
// does NOT import `_shared/lineupLocks.ts` — it has its own `checkGameStartLock`,
// whose predicate is `status !== 'scheduled' && status !== 'postponed'` (so
// final/live/unknown all lock) and which queries the caller-supplied game_date.
// Proven live on staging: a swap on a final game is refused 400, the same swap on
// a scheduled game returns 200. The Title Case module reaches only
// commissioner-override; that is SAB-1157.

import { isKnownUnplayable } from '@shared/mlbPlayerStatus'

export type LockablePlayer = {
  player_id: string
  mlb_team_id: number | null
  mlb_team_abbreviation: string | null
  /** Roster status. A KNOWN-unplayable value unlocks; unknown still fails closed. */
  player_status?: string | null
}

export type ScheduleGame = {
  home_team_id: number | null
  away_team_id: number | null
  start_time: string | null
  status: string | null
}

export const UNVERIFIABLE_LOCK_REASON =
  "Locked — could not verify this player's game status"

/** Definitively under way or over — the slot is locked regardless of start_time. */
const STARTED_STATUSES = new Set(['live', 'final'])

/** Not yet under way — locked only once start_time has passed. */
const NOT_STARTED_STATUSES = new Set(['scheduled'])

/**
 * Postponed: the game will not be played today.
 *
 * DELIBERATE DECISION (not a default): treat as LOCKED. A postponed game is
 * frequently resumed or replayed as part of a same-day doubleheader, and we
 * cannot tell the difference from this row alone. Leaving it unlocked would let
 * someone move a player after seeing how the situation developed. The cost of
 * locking is that a user cannot swap a player who will score nothing — mildly
 * annoying, and safe. The cost of unlocking is retroactive manipulation on a
 * real-money contest. Flip this only with a product decision, not silently.
 */
const POSTPONED_STATUSES = new Set(['postponed'])

/**
 * Does this game mean its teams' slots are locked?
 *
 * Unrecognised statuses return TRUE (locked). `suspended` and `delayed` are not
 * currently emitted by either writer, but if one ever appears it must lock
 * rather than silently read as "not started".
 */
export function gameLocksItsTeams(game: ScheduleGame, now: Date): boolean {
  const status = (game.status ?? '').trim().toLowerCase()

  if (STARTED_STATUSES.has(status)) return true
  if (POSTPONED_STATUSES.has(status)) return true

  if (NOT_STARTED_STATUSES.has(status)) {
    // The status column lags the clock: a game past its start_time is under way
    // even while still marked scheduled.
    if (!game.start_time) return true // no start time to reason about -> fail closed
    return new Date(game.start_time).getTime() <= now.getTime()
  }

  // Invariant 3: unknown / empty / null status -> LOCKED.
  return true
}

/** Collect the team ids whose game today locks their players. */
export function collectLockedTeamIds(games: readonly ScheduleGame[], now: Date): Set<number> {
  const ids = new Set<number>()
  for (const g of games) {
    if (!gameLocksItsTeams(g, now)) continue
    if (g.home_team_id != null) ids.add(g.home_team_id)
    if (g.away_team_id != null) ids.add(g.away_team_id)
  }
  return ids
}

/**
 * Build the player_id -> lock-reason map for per-game lock mode.
 *
 * @param roster         the team's roster rows
 * @param lockedTeamIds  MLB team ids whose game today locks their players
 */
export function computePlayerLocks(
  roster: readonly LockablePlayer[],
  lockedTeamIds: ReadonlySet<number>,
): Map<string, string> {
  const locks = new Map<string, string>()

  for (const player of roster) {
    const teamId = player.mlb_team_id

    // Invariant 4: a player who CANNOT PLAY is not locked.
    //
    // Tested before the team check on purpose. These players frequently have a
    // stale or absent team -- that is exactly why they were reaching invariant 1
    // and freezing a roster slot indefinitely. The status is the stronger fact:
    // "Released" tells us more than a team pointer does, and it tells us the one
    // thing the lock cares about. See @shared/mlbPlayerStatus for why the list is
    // narrow and why an unrecognised status deliberately does NOT unlock.
    if (isKnownUnplayable(player.player_status)) {
      continue
    }

    // Invariant 1: unresolvable team => LOCKED, never unlocked.
    if (teamId == null) {
      locks.set(player.player_id, UNVERIFIABLE_LOCK_REASON)
      continue
    }

    if (lockedTeamIds.has(teamId)) {
      const abbr = player.mlb_team_abbreviation ?? 'Team'
      locks.set(player.player_id, `${abbr} game started`)
    }
  }

  return locks
}
